/**
 * The ticket state machine.
 *
 * This module is imported by the API, the worker and the web app so all three
 * agree on what is possible. It is NOT the enforcement point. The database
 * holds the authoritative copy in `ticket_status_transitions` and rejects
 * anything not listed there, so a bug here can only make the UI wrong, never
 * let an illegal transition reach the database.
 *
 * Keep TRANSITIONS in sync with the seed block in the integrity migration.
 * `assertTransitionTableMatchesDatabase()` is run as a startup check.
 */
export const TICKET_STATUSES = [
    'NEW',
    'TRIAGED',
    'ASSIGNED',
    'IN_PROGRESS',
    'PENDING_USER',
    'PENDING_EXTERNAL',
    'RESOLVED',
    'REOPENED',
    'CLOSED',
    'CANCELLED',
];
/**
 * Named business actions. A request may name an INTENT; it may never name a
 * target status. This is what stops a request body from driving an arbitrary
 * status write.
 */
export const INTENTS = [
    'TRIAGE',
    'RETRIAGE',
    'ASSIGN',
    'REASSIGN',
    'RETURN_TO_TRIAGE',
    'START_WORK',
    'RESUME_WORK',
    'MARK_PENDING_USER',
    'MARK_PENDING_EXT',
    'RESOLVE',
    'CONFIRM_RESOLUTION',
    'REJECT_RESOLUTION',
    'CLOSE',
    'REOPEN',
    'CANCEL',
    'RESTORE',
    /**
     * NOT a status transition — deliberately absent from `TRANSITIONS` below.
     * Escalation (docs/API.md §4) is modelled as an audited reassignment
     * (`ticket_assignments.is_escalation`) plus an optional team/technician
     * handoff, never a status change, so it never appears in `intentsFrom()`.
     * `availableIntentsFor` (`ticket.mapper.ts`) adds it explicitly whenever
     * the ticket is not in a terminal status and the actor holds
     * `ticket:escalate` — see that function's comment.
     */
    'ESCALATE',
];
export const TRANSITIONS = [
    { from: 'NEW', to: 'TRIAGED', intent: 'TRIAGE', permission: 'ticket:triage' },
    { from: 'NEW', to: 'ASSIGNED', intent: 'ASSIGN', permission: 'ticket:assign:team' },
    { from: 'NEW', to: 'CANCELLED', intent: 'CANCEL', permission: 'ticket:cancel' },
    { from: 'TRIAGED', to: 'ASSIGNED', intent: 'ASSIGN', permission: 'ticket:assign:team' },
    { from: 'TRIAGED', to: 'TRIAGED', intent: 'RETRIAGE', permission: 'ticket:triage' },
    { from: 'TRIAGED', to: 'CANCELLED', intent: 'CANCEL', permission: 'ticket:cancel' },
    { from: 'ASSIGNED', to: 'ASSIGNED', intent: 'REASSIGN', permission: 'ticket:assign:team' },
    { from: 'ASSIGNED', to: 'IN_PROGRESS', intent: 'START_WORK', permission: 'ticket:status:transition' },
    { from: 'ASSIGNED', to: 'TRIAGED', intent: 'RETURN_TO_TRIAGE', permission: 'ticket:triage' },
    { from: 'ASSIGNED', to: 'PENDING_USER', intent: 'MARK_PENDING_USER', permission: 'ticket:status:transition' },
    { from: 'ASSIGNED', to: 'CANCELLED', intent: 'CANCEL', permission: 'ticket:cancel' },
    { from: 'IN_PROGRESS', to: 'PENDING_USER', intent: 'MARK_PENDING_USER', permission: 'ticket:status:transition' },
    { from: 'IN_PROGRESS', to: 'PENDING_EXTERNAL', intent: 'MARK_PENDING_EXT', permission: 'ticket:status:transition' },
    {
        from: 'IN_PROGRESS',
        to: 'RESOLVED',
        intent: 'RESOLVE',
        permission: 'ticket:resolve',
        guards: ['RESOLUTION_RECORDED', 'RESOLVER_ATTESTED'],
    },
    { from: 'IN_PROGRESS', to: 'ASSIGNED', intent: 'REASSIGN', permission: 'ticket:assign:team' },
    { from: 'IN_PROGRESS', to: 'CANCELLED', intent: 'CANCEL', permission: 'ticket:cancel' },
    { from: 'PENDING_USER', to: 'IN_PROGRESS', intent: 'RESUME_WORK', permission: 'ticket:status:transition' },
    {
        from: 'PENDING_USER',
        to: 'RESOLVED',
        intent: 'RESOLVE',
        permission: 'ticket:resolve',
        guards: ['RESOLUTION_RECORDED', 'RESOLVER_ATTESTED'],
    },
    { from: 'PENDING_USER', to: 'CANCELLED', intent: 'CANCEL', permission: 'ticket:cancel' },
    { from: 'PENDING_EXTERNAL', to: 'IN_PROGRESS', intent: 'RESUME_WORK', permission: 'ticket:status:transition' },
    {
        from: 'PENDING_EXTERNAL',
        to: 'RESOLVED',
        intent: 'RESOLVE',
        permission: 'ticket:resolve',
        guards: ['RESOLUTION_RECORDED', 'RESOLVER_ATTESTED'],
    },
    { from: 'PENDING_EXTERNAL', to: 'CANCELLED', intent: 'CANCEL', permission: 'ticket:cancel' },
    // The two-sided gate. No permission opens this on its own.
    {
        from: 'RESOLVED',
        to: 'CLOSED',
        intent: 'CLOSE',
        permission: null,
        guards: ['RESOLUTION_RECORDED', 'RESOLVER_ATTESTED', 'EFFECTIVE_USER_CONFIRMATION', 'CLOSURE_RECORD'],
    },
    {
        from: 'RESOLVED',
        to: 'REOPENED',
        intent: 'REJECT_RESOLUTION',
        permission: 'ticket:confirm-resolution:own',
        guards: ['ACTOR_IS_REQUESTER'],
    },
    { from: 'REOPENED', to: 'ASSIGNED', intent: 'ASSIGN', permission: 'ticket:assign:team' },
    { from: 'REOPENED', to: 'IN_PROGRESS', intent: 'START_WORK', permission: 'ticket:status:transition' },
    { from: 'REOPENED', to: 'CANCELLED', intent: 'CANCEL', permission: 'ticket:cancel' },
    { from: 'CLOSED', to: 'REOPENED', intent: 'REOPEN', permission: 'ticket:reopen', guards: ['WITHIN_REOPEN_WINDOW'] },
    {
        from: 'CANCELLED',
        to: 'TRIAGED',
        intent: 'RESTORE',
        permission: 'ticket:admin:restore',
        guards: ['ADMIN_OVERRIDE_REASON'],
    },
];
/** Statuses from which no further work happens without an explicit reopen. */
export const TERMINAL_STATUSES = ['CLOSED', 'CANCELLED'];
/** Statuses an SLA resolution clock is still running against. */
export const OPEN_STATUSES = [
    'NEW',
    'TRIAGED',
    'ASSIGNED',
    'IN_PROGRESS',
    'PENDING_USER',
    'PENDING_EXTERNAL',
    'REOPENED',
];
export class IllegalTransitionError extends Error {
    from;
    to;
    intent;
    code = 'INVALID_TRANSITION';
    constructor(from, to, intent) {
        super(intent
            ? `Intent ${intent} is not valid from status ${from}.`
            : `Transition ${from} -> ${to} is not permitted.`);
        this.from = from;
        this.to = to;
        this.intent = intent;
        this.name = 'IllegalTransitionError';
    }
}
const byIntent = new Map();
for (const t of TRANSITIONS)
    byIntent.set(`${t.from}::${t.intent}`, t);
const allowed = new Set(TRANSITIONS.map((t) => `${t.from}::${t.to}`));
/** Resolve a named intent against the current status. Never accepts a raw target status. */
export function resolveIntent(from, intent) {
    const t = byIntent.get(`${from}::${intent}`);
    if (!t)
        throw new IllegalTransitionError(from, from, intent);
    return t;
}
export function canTransition(from, to) {
    return allowed.has(`${from}::${to}`);
}
export function assertTransition(from, to) {
    if (!canTransition(from, to))
        throw new IllegalTransitionError(from, to);
}
export function transitionsFrom(from) {
    return TRANSITIONS.filter((t) => t.from === from);
}
export function intentsFrom(from) {
    return transitionsFrom(from).map((t) => t.intent);
}
/**
 * "Awaiting user confirmation" is derived in exactly one place. Dashboards,
 * queues and reports all call this rather than re-deriving the condition, so
 * the definition cannot drift between surfaces.
 */
export function isAwaitingUserConfirmation(t) {
    return t.status === 'RESOLVED' && t.userConfirmationStatus === 'PENDING' && !t.deletedAt;
}
/**
 * RESOLVED is not CLOSED. Reporting must not treat a resolved ticket as done,
 * and must not count an auto-close or an administrative override as the
 * requester having confirmed anything.
 */
export function wasUserConfirmed(userConfirmationStatus) {
    return userConfirmationStatus === 'CONFIRMED';
}
export function isClosedWithoutUserConfirmation(t) {
    return (t.status === 'CLOSED' &&
        (t.userConfirmationStatus === 'AUTO_CONFIRMED' || t.userConfirmationStatus === 'ADMIN_OVERRIDE'));
}
/**
 * Startup check: this table and the database's must be identical. If they ever
 * diverge, the UI will offer actions the database rejects.
 */
export function diffAgainstDatabase(dbRows) {
    const dbSet = new Set(dbRows.map((r) => `${r.fromStatus}::${r.toStatus}`));
    return {
        missingInCode: [...dbSet].filter((k) => !allowed.has(k)),
        missingInDatabase: [...allowed].filter((k) => !dbSet.has(k)),
    };
}
//# sourceMappingURL=ticket-state-machine.js.map